/**
 * Webinars.tsx — v2 Webinar analytics
 *
 * Architecture:
 *  - Optins: urban_unity_webinar_optins (by assignedWebinarId) + main urban_unity_optins (date+UTM filter)
 *  - Apps/Calls/Cash: matched by email from main collections
 *  - Zoom: uploaded CSV per webinar
 *  - Paid = utm_source = "paid ads"
 *
 * Features:
 *  - Clickable funnel blocks → lead list modal → lead timeline drawer
 *  - In-funnel All/Compare toggle (organic vs paid side-by-side)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { branding } from "../config/branding";

// ─── Types ───────────────────────────────────────────────────────────────────

interface WebinarDef {
  id: string;
  name: string;
  date: string;
}

// Enhanced metrics types
interface SalesMetricsBlock {
  optins: number; apps: number; fullApps: number; partialApps: number;
  bookedCalls: number; takenCalls: number; closed: number;
  showRate: number; closeRate: number; cash: number; auv: number;
  cashPerCall: number; costPerBooking: number; appToBookingRate: number;
  chargebackRate: number; roas: number;
}

interface OnWebinarMetrics {
  registrants: number; cpr: number; attended: number; showRate: number;
  avgDuration: number; bookingRate: number; pitchMinute: number | null;
  retentionAtPitch: number;
}

interface LowTicketMetrics {
  purchasers: number; purchaseRate: number; liquidationPct: number; auv: number;
  purchaserWebinarShowRate: number; purchaserToBookingRate: number;
  purchaserToShowRate: number; purchaserToCloseRate: number; totalRevenue: number;
}

interface DialMetrics {
  totalDials: number; pickupRate: number; callsBooked: number;
}

interface EnhancedWebinarData {
  webinarId: string; webinarName: string; webinarDate: string;
  adSpend: number; hasZoomData: boolean;
  salesTotal: SalesMetricsBlock; salesOnWebby: SalesMetricsBlock; salesPostWebby: SalesMetricsBlock;
  onWebinar: OnWebinarMetrics; lowTicket: LowTicketMetrics; dials: DialMetrics;
  webinarStartTime: string | null; webinarEndTime: string | null;
}

interface WebinarFunnel {
  webinarId: string;
  webinarName: string;
  webinarDate: string;
  totalRegistrants: number;
  totalAttended: number;
  showRate: number;
  avgDuration: number;
  totalOptins: number;
  paidOptins: number;
  organicOptins: number;
  totalApps: number;
  paidApps: number;
  organicApps: number;
  totalCalls: number;
  paidCalls: number;
  organicCalls: number;
  totalShowed: number;
  paidShowed: number;
  organicShowed: number;
  totalWins: number;
  paidWins: number;
  organicWins: number;
  totalCash: number;
  paidCash: number;
  organicCash: number;
  optinToAppRate: number;
  appToCallRate: number;
  callShowRate: number;
  callToCloseRate: number;
  hasZoomData: boolean;
  adSpend: number;
  roas: number;
}

interface RetentionPoint {
  minute: number;
  count: number;
  pct: number;
}

interface RetentionBySourcePoint {
  minute: number;
  [source: string]: number;
}

interface CashBySourceItem {
  source: string;
  cash: number;
  wins: number;
}

interface FunnelStage {
  label: string;
  value: number;
  subLabel?: string;
  isCash?: boolean;
}

interface StageLead {
  email: string;
  name: string;
  source: string;
  outcome?: string;
  cash?: number;
  closedAt?: string;
  webinarOptinCount?: number;
}

// Lead timeline types (mirrored from Leads.tsx)
interface TimelineOptin {
  date: string; source: string; medium: string; campaign: string; content: string; sheet: string;
}
interface TimelineApplication {
  date: string; source: string; medium: string; campaign: string; content: string; responseType: string;
}
interface TimelineCall {
  date: string; outcome: string; closer: string; setter: string; cash: number; offer: string;
  notes: string; fathomLink: string; platform: string; funnel: string;
}
interface TimelineWebinarOptin {
  date: string; webinarName: string; webinarId: string; source: string; medium: string; campaign: string;
}
interface TimelineMeetingBooking {
  bookedAt: string; meetingAt: string; closer: string;
}
interface LeadTimelineData {
  email: string; name: string; phone: string; country: string; leadType: string;
  profile: { hasBrand: string; currentMRR: string; ableToInvest: string };
  optins: TimelineOptin[];
  application: TimelineApplication | null;
  calls: TimelineCall[];
  webinarOptins: TimelineWebinarOptin[];
  meetingBookings: TimelineMeetingBooking[];
  totalRevenue: number;
  isClosed: boolean;
}

// Stage label → API key mapping
const STAGE_KEY_MAP: Record<string, string> = {
  "Opt-ins": "optins",
  "Registrants": "registrants",
  "Attended": "attended",
  "Applications": "apps",
  "Full Apps": "apps",
  "Partial Apps": "apps",
  "Calls Booked": "calls",
  "Calls Taken": "showed",
  "Closed": "wins",
  "Closes": "wins",
  "Cash": "wins",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtN = (n: number) => Math.round(n).toLocaleString();

const formatDate = (s: string) => {
  const d = new Date(s + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

function fmtDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── OutcomeBadge ─────────────────────────────────────────────────────────────

function OutcomeBadge({ outcome }: { outcome: string }) {
  const closed = new Set(["Closed", "closed", "Close", "close", "Won", "won", "Sale", "sale"]);
  const noshow = new Set(["No Show", "Cancelled", "Rescheduled", "Cancelled by sales team"]);
  const isWon = closed.has(outcome);
  const isNoShow = noshow.has(outcome) || !outcome;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      isWon ? "bg-emerald-500/15 text-emerald-400" :
      isNoShow ? "bg-zinc-500/15 text-zinc-400" :
      "bg-amber-500/15 text-amber-400"
    }`}>
      {outcome || "No Show"}
    </span>
  );
}

// ─── Lead Timeline Drawer ─────────────────────────────────────────────────────

function LeadTimelineDrawer({ email, onClose }: { email: string; onClose: () => void }) {
  const [data, setData] = useState<LeadTimelineData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetch(`/api/lead-timeline?email=${encodeURIComponent(email)}`)
      .then((r) => r.json() as Promise<LeadTimelineData>)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const pri = branding.colors.primary;
  const sec = branding.colors.secondary;

  const events = data ? [
    ...data.optins.map((o) => ({ type: "optin" as const, date: o.date, data: o })),
    ...(data.webinarOptins ?? []).map((w) => ({ type: "webinar" as const, date: w.date, data: w })),
    ...(data.meetingBookings ?? []).map((m) => ({ type: "meeting" as const, date: m.bookedAt, data: m })),
    ...(data.application ? [{ type: "app" as const, date: data.application.date, data: data.application }] : []),
    ...data.calls.map((c) => ({ type: "call" as const, date: c.date, data: c })),
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return -1;
    if (!b.date) return 1;
    return a.date.localeCompare(b.date);
  }) : [];

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-card border-l border-border z-[70] flex flex-col overflow-hidden"
        style={{ boxShadow: "-8px 0 40px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground truncate">{data?.name || email}</h2>
              {data?.isClosed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Closed Won
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{email}</p>
            {data && (data.phone || data.country) && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {[data.phone, data.country].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <button onClick={onClose}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* KPI strip */}
        {data && (
          <div className="flex gap-3 px-5 py-3 border-b border-border shrink-0">
            {[
              { label: "Optins",   value: data.optins.length + (data.webinarOptins?.length ?? 0) },
              { label: "Applied",  value: data.application ? "Yes" : "No" },
              { label: "Meetings", value: (data.meetingBookings?.length ?? 0) + data.calls.length },
              { label: "Revenue",  value: data.totalRevenue > 0 ? `$${data.totalRevenue.toLocaleString()}` : "$0" },
            ].map((k) => (
              <div key={k.label} className="flex-1 min-w-0 rounded-xl px-3 py-2 text-center"
                style={{
                  background: `rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.06)`,
                  border: `1px solid rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.12)`,
                }}>
                <div className="text-[10px] text-muted-foreground font-medium mb-0.5">{k.label}</div>
                <div className="text-[15px] font-bold text-foreground tabular-nums">{String(k.value)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse mr-2" style={{ background: pri }} />
              Loading timeline…
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              No events found for this lead.
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="relative">
              <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border" />
              <div className="space-y-5">
                {events.map((ev, i) => {
                  const isOptin   = ev.type === "optin";
                  const isApp     = ev.type === "app";
                  const isCall    = ev.type === "call";
                  const isWebinar = ev.type === "webinar";
                  const isMeeting = ev.type === "meeting";
                  const isClosed  = isCall && new Set(["Closed","closed","Close","close","Won","won","Sale","sale"]).has((ev.data as TimelineCall).outcome);
                  const dotColor  = isOptin ? sec : isApp ? pri : isWebinar ? "#f59e0b" : isMeeting ? "#8b5cf6" : isClosed ? "#10b981" : "var(--muted-foreground)";
                  const label     = isOptin ? "Opted In" : isApp ? "Submitted Application" : isWebinar ? "Webinar Registration" : isMeeting ? "Meeting Booked" : "Call";

                  return (
                    <div key={i} className="flex gap-4 relative">
                      <div className="shrink-0 w-10 flex flex-col items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-background flex items-center justify-center z-10" style={{ background: dotColor }}>
                          {isOptin   && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2a5 5 0 110 10A5 5 0 0112 2zm0 12c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z"/></svg>}
                          {isApp     && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                          {isCall    && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>}
                          {isWebinar && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.07A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14v-4zM3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>}
                          {isMeeting && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 rounded-xl border border-border/60 bg-background/60 px-4 py-3 mb-1" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-foreground">{label}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{ev.date ? fmtDate(ev.date) : "—"}</span>
                        </div>
                        {isOptin && (() => {
                          const o = ev.data as TimelineOptin;
                          return (
                            <div className="space-y-0.5">
                              {o.source   && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{o.source}</span></div>}
                              {o.medium   && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{o.medium}</span></div>}
                              {o.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{o.campaign}</span></div>}
                              {o.sheet    && <div className="text-[10px] text-muted-foreground">Sheet: <span className="text-foreground/80">{o.sheet}</span></div>}
                            </div>
                          );
                        })()}
                        {isWebinar && (() => {
                          const w = ev.data as TimelineWebinarOptin;
                          return (
                            <div className="space-y-0.5">
                              {w.webinarName && <div className="text-[10px] text-muted-foreground">Webinar: <span className="text-foreground/80">{w.webinarName}</span></div>}
                              {w.source     && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{w.source}</span></div>}
                              {w.campaign   && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{w.campaign}</span></div>}
                            </div>
                          );
                        })()}
                        {isMeeting && (() => {
                          const m = ev.data as TimelineMeetingBooking;
                          return (
                            <div className="space-y-0.5">
                              {m.meetingAt && <div className="text-[10px] text-muted-foreground">Scheduled for: <span className="text-foreground/80">{fmtDate(m.meetingAt)}</span></div>}
                              {m.closer    && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{m.closer}</span></div>}
                            </div>
                          );
                        })()}
                        {isApp && (() => {
                          const a = ev.data as TimelineApplication;
                          return (
                            <div className="space-y-0.5">
                              {a.source       && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{a.source}</span></div>}
                              {a.medium       && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{a.medium}</span></div>}
                              {a.responseType && <div className="text-[10px] text-muted-foreground">Type: <span className="text-foreground/80">{a.responseType}</span></div>}
                            </div>
                          );
                        })()}
                        {isCall && (() => {
                          const c = ev.data as TimelineCall;
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <OutcomeBadge outcome={c.outcome} />
                                {c.cash > 0 && <span className="text-[10px] font-semibold text-emerald-400">${c.cash.toLocaleString()}</span>}
                              </div>
                              {c.offer  && <div className="text-[10px] text-muted-foreground">Offer: <span className="text-foreground/80">{c.offer}</span></div>}
                              {c.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{c.closer}</span></div>}
                              {c.setter && <div className="text-[10px] text-muted-foreground">Setter: <span className="text-foreground/80">{c.setter}</span></div>}
                              {c.notes  && <div className="text-[10px] text-muted-foreground mt-1 italic">"{c.notes}"</div>}
                              {c.fathomLink && (
                                <a href={c.fathomLink} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] mt-1"
                                  style={{ color: pri }}>
                                  View recording ↗
                                </a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// ─── Retention By Source Chart (with click-to-toggle lines) ──────────────────

function RetentionBySourceChart({
  data,
  sources,
  colors,
}: {
  data: RetentionBySourcePoint[];
  sources: string[];
  colors: string[];
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (src: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next;
    });
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">Retention by traffic source</p>

      {/* Clickable legend */}
      <div className="flex flex-wrap gap-2 mb-3">
        {sources.map((src, i) => {
          const isHidden = hidden.has(src);
          const color = colors[i % colors.length];
          return (
            <button
              key={src}
              onClick={() => toggle(src)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[10px] font-medium transition-all ${
                isHidden
                  ? "border-border/30 bg-muted/20 text-muted-foreground/40 line-through"
                  : "border-border bg-muted/40 text-foreground"
              }`}
            >
              <span
                className="w-3 h-0.5 rounded-full shrink-0"
                style={{ background: isHidden ? "rgba(255,255,255,0.15)" : color }}
              />
              {src}
            </button>
          );
        })}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${v}m`} />
          <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            labelFormatter={(v) => `${v} min`}
            formatter={(v: number, name: string) => hidden.has(name) ? [] : [`${v.toFixed(1)}%`, name]}
          />
          {sources.map((src, i) => (
            <Line
              key={src}
              type="monotone"
              dataKey={src}
              name={src}
              stroke={colors[i % colors.length]}
              strokeWidth={src === "all" ? 2 : 1.5}
              strokeDasharray={src === "all" ? "0" : undefined}
              dot={false}
              hide={hidden.has(src)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Cash By Source Pie Chart ────────────────────────────────────────────────

function CashBySourceChart({ data }: { data: CashBySourceItem[] }) {
  const colors = branding.colors.chart;
  const total = data.reduce((s, d) => s + d.cash, 0);

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
    cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number;
  }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">Cash collected by traffic source</p>
      <div className="flex flex-col md:flex-row items-center gap-6">
        <ResponsiveContainer width={280} height={280}>
          <PieChart>
            <Pie
              data={data}
              dataKey="cash"
              nameKey="source"
              cx="50%"
              cy="50%"
              outerRadius={115}
              labelLine={false}
              label={renderLabel}
            >
              {data.map((entry, i) => (
                <Cell key={entry.source} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v: number, name: string) => [`$${v.toLocaleString()}`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col gap-3 min-w-0">
          {data.map((entry, i) => (
            <div key={entry.source} className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} />
              <span className="text-sm text-muted-foreground min-w-[90px]">{entry.source}</span>
              <span className="text-sm font-bold text-foreground">${entry.cash.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground/60">
                {total > 0 ? `${((entry.cash / total) * 100).toFixed(0)}%` : ""}
              </span>
              <span className="text-xs text-muted-foreground/40">{entry.wins}w</span>
            </div>
          ))}
          <div className="mt-1 pt-2 border-t border-border/20 flex items-center gap-3">
            <span className="text-sm font-bold text-foreground">${total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/50">total</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Funnel Chart (inverted triangle, clickable) ─────────────────────────────

function FunnelChart({
  stages,
  gradientId = "fgA",
  onStageClick,
}: {
  stages: FunnelStage[];
  gradientId?: string;
  onStageClick?: (label: string) => void;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const FULL_W = 480;
  const STAGE_H = 58;
  const TIP_W = 52;
  const GAP = 2;
  const LABEL_PAD = 90;
  const CONV_PAD = 56;
  const totalW = FULL_W + LABEL_PAD + CONV_PAD;
  const totalH = stages.length * (STAGE_H + GAP);

  // Fixed triangle shape — evenly stepped from full width down to a point, ignoring data values
  const n = stages.length;
  const stageWidths = stages.map((_, i) => {
    const t = n <= 1 ? 1 : i / (n - 1);
    return Math.round(FULL_W - t * (FULL_W - TIP_W));
  });

  return (
    <svg
      viewBox={`0 0 ${totalW} ${totalH}`}
      className="w-full"
      style={{ height: Math.min(totalH, 460) }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={branding.colors.gradient.start} stopOpacity={0.92} />
          <stop offset="100%" stopColor={branding.colors.gradient.end} stopOpacity={0.92} />
        </linearGradient>
      </defs>

      {stages.map((stage, i) => {
        const topW    = stageWidths[i];
        const bottomW = i < stages.length - 1 ? stageWidths[i + 1] : 0;
        const funnelLeft  = LABEL_PAD;
        const topLeft     = funnelLeft + (FULL_W - topW) / 2;
        const topRight    = funnelLeft + (FULL_W + topW) / 2;
        const bottomLeft  = funnelLeft + (FULL_W - bottomW) / 2;
        const bottomRight = funnelLeft + (FULL_W + bottomW) / 2;
        const y  = i * (STAGE_H + GAP);
        const cy = y + STAGE_H / 2;
        const pts = `${topLeft},${y} ${topRight},${y} ${bottomRight},${y + STAGE_H} ${bottomLeft},${y + STAGE_H}`;

        const convPct =
          i > 0 && i < stages.length - 1 && stages[i - 1].value > 0
            ? `${Math.round((stage.value / stages[i - 1].value) * 100)}%`
            : null;

        const opacity  = Math.max(0.5, 1 - i * 0.055);
        const isHovered = hoveredIdx === i;

        return (
          <g
            key={stage.label}
            onClick={() => onStageClick?.(stage.label)}
            onMouseEnter={() => onStageClick && setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: onStageClick ? "pointer" : "default" }}
          >
            <polygon points={pts} fill={`url(#${gradientId})`} opacity={opacity} />

            {/* Hover overlay */}
            {isHovered && <polygon points={pts} fill="white" opacity={0.1} />}

            {/* Stage label — left */}
            <text
              x={topLeft - 8} y={cy + 1}
              textAnchor="end" dominantBaseline="middle"
              fontSize={11} fontFamily="sans-serif"
              fill="rgba(255,255,255,0.55)"
            >
              {stage.label}
            </text>

            {/* Primary value — center */}
            <text
              x={funnelLeft + FULL_W / 2}
              y={stage.subLabel ? cy - 7 : cy}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={14} fontWeight="bold" fontFamily="sans-serif"
              fill="white"
            >
              {stage.isCash ? fmt$(stage.value) : fmtN(stage.value)}
            </text>

            {stage.subLabel && (
              <text
                x={funnelLeft + FULL_W / 2} y={cy + 10}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={9} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.55)"
              >
                {stage.subLabel}
              </text>
            )}

            {/* "click to view leads" hint on hover */}
            {isHovered && onStageClick && (
              <text
                x={funnelLeft + FULL_W / 2}
                y={stage.subLabel ? cy + 22 : cy + 14}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={8} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.65)"
              >
                click to view leads
              </text>
            )}

            {/* Conversion % — right */}
            {convPct && (
              <text
                x={topRight + 8} y={cy + 1}
                textAnchor="start" dominantBaseline="middle"
                fontSize={10} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.4)"
              >
                {convPct}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Funnel Leads Modal ───────────────────────────────────────────────────────

interface FunnelLeadsModalProps {
  webinarId: string;
  webinarName: string;
  stageLabel: string;
  stageKey: string;
  traffic: "all" | "paid" | "organic";
  onClose: () => void;
  onLeadClick: (email: string) => void;
}

function FunnelLeadsModal({
  webinarId, webinarName, stageLabel, stageKey, traffic, onClose, onLeadClick,
}: FunnelLeadsModalProps) {
  const [leads, setLeads] = useState<StageLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    setLeads([]);
    fetch(`/api/webinar2/stage-leads/${webinarId}/${stageKey}?traffic=${traffic}`)
      .then((r) => r.json())
      .then((d) => { setLeads(d.leads || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [webinarId, stageKey, traffic]);

  const filtered = search
    ? leads.filter(
        (l) =>
          l.email.toLowerCase().includes(search.toLowerCase()) ||
          l.name.toLowerCase().includes(search.toLowerCase())
      )
    : leads;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl w-full max-w-lg z-10 flex flex-col"
        style={{ maxHeight: "80vh" }}
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm font-bold text-foreground">{stageLabel} Leads</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {webinarName}
              {!loading && ` · ${leads.length.toLocaleString()} leads`}
              {traffic !== "all" && ` · ${traffic}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-border/50 shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
            autoFocus
          />
        </div>

        {/* Lead list */}
        <div className="flex-1 overflow-y-auto py-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              {search ? "No leads match your search" : "No leads for this stage"}
            </div>
          ) : (
            <>
              {filtered.map((lead) => (
                <button
                  key={lead.email}
                  onClick={() => onLeadClick(lead.email)}
                  className="w-full text-left px-4 py-2.5 hover:bg-muted/30 transition-colors flex items-center justify-between gap-3 group border-b border-border/20 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {lead.name || lead.email}
                    </div>
                    {lead.name && (
                      <div className="text-[11px] text-muted-foreground truncate">{lead.email}</div>
                    )}
                    {lead.source && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{lead.source}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {lead.webinarOptinCount != null && lead.webinarOptinCount > 0 && (
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          lead.webinarOptinCount >= 3
                            ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                            : "bg-muted text-muted-foreground"
                        }`}
                        title={`${lead.webinarOptinCount} webinar optin${lead.webinarOptinCount !== 1 ? "s" : ""}`}
                      >
                        ×{lead.webinarOptinCount}
                      </span>
                    )}
                    {lead.closedAt && (
                      <span className="text-[10px] text-muted-foreground">{lead.closedAt}</span>
                    )}
                    {lead.outcome && <OutcomeBadge outcome={lead.outcome} />}
                    {lead.cash != null && lead.cash > 0 && (
                      <span className="text-xs font-semibold text-emerald-400">
                        ${lead.cash.toLocaleString()}
                      </span>
                    )}
                    <svg className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
              {leads.length >= 500 && search === "" && (
                <p className="text-center py-3 text-[10px] text-muted-foreground">
                  Showing first 500 leads · use search to find specific leads
                </p>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Zoom Upload Modal ────────────────────────────────────────────────────────

interface UploadModalProps {
  webinarId: string;
  webinarName: string;
  onClose: () => void;
  onSuccess: (count: number) => void;
}

function ZoomUploadModal({ webinarId, webinarName, onClose, onSuccess }: UploadModalProps) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [result, setResult] = useState<{ imported: number; attended: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    if (!f.name.endsWith(".csv")) { setErrorMsg("Please upload a CSV file."); return; }
    setFile(f);
    setErrorMsg("");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/webinar2/zoom-upload/${webinarId}`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success) {
        setResult({ imported: data.imported, attended: data.attended });
        setStatus("success");
        onSuccess(data.imported);
      } else {
        setErrorMsg(data.error || "Upload failed");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Network error");
      setStatus("error");
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md z-10"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Upload Zoom Report</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{webinarName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
        </div>

        {status === "success" ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-foreground font-semibold">Upload complete</p>
            <p className="text-muted-foreground text-sm mt-1">
              {result?.imported} registrants imported · {result?.attended} attended
            </p>
            <button onClick={onClose} className="mt-4 px-5 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium">
              Done
            </button>
          </div>
        ) : (
          <>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragging ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-border hover:border-border/70"
              }`}
            >
              <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
              <div className="text-2xl mb-2">📂</div>
              {file ? (
                <p className="text-sm text-foreground font-medium">{file.name}</p>
              ) : (
                <>
                  <p className="text-sm text-foreground font-medium">Drop CSV or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">Zoom attendee/registrant report</p>
                </>
              )}
            </div>
            {errorMsg && <p className="text-red-400 text-xs mt-2">{errorMsg}</p>}
            <p className="text-xs text-muted-foreground mt-3">
              Required columns: Attended, Email, First Name, Last Name, Time in Session (minutes), Registration Time, Join Time, Leave Time
            </p>
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || status === "uploading"}
                className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              >
                {status === "uploading" ? "Uploading…" : "Upload"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Add Webinar Modal ────────────────────────────────────────────────────────

interface AddWebinarModalProps {
  onClose: () => void;
  onSuccess: (webinarId: string) => void;
}

function AddWebinarModal({ onClose, onSuccess }: AddWebinarModalProps) {
  const [date, setDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [detectedDate, setDetectedDate] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ webinarId: string; imported: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sniff date from CSV header client-side so user sees it before submitting
  const handleFileChange = (f: File) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/).slice(0, 6);
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].toLowerCase().includes("topic") && i + 1 < lines.length) {
          const headerCols = lines[i].split(",").map((h) => h.trim().toLowerCase());
          const dataRow = lines[i + 1].split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
          const startIdx = headerCols.findIndex((h) => h.includes("actual start time") || h.includes("start time"));
          if (startIdx >= 0 && dataRow[startIdx]) {
            const d = new Date(dataRow[startIdx]);
            if (!isNaN(d.getTime())) {
              const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              setDetectedDate(iso);
              if (!date) setDate(iso);
            }
          }
          break;
        }
      }
    };
    reader.readAsText(f);
  };

  const handleSubmit = async () => {
    const effectiveDate = date || detectedDate;
    if (!effectiveDate && !file) { setError("Upload a Zoom CSV or enter a date"); return; }
    setStatus("loading");
    setError("");
    try {
      const fd = new FormData();
      if (effectiveDate) fd.append("date", effectiveDate);
      if (file) fd.append("file", file);
      const res = await fetch("/api/webinar2/create", { method: "POST", body: fd });
      const data = await res.json() as { success: boolean; webinarId?: string; imported?: number; error?: string };
      if (data.success && data.webinarId) {
        setResult({ webinarId: data.webinarId, imported: data.imported ?? 0 });
        setStatus("success");
      } else {
        setError(data.error ?? "Failed to create webinar");
        setStatus("error");
      }
    } catch {
      setError("Network error");
      setStatus("error");
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md z-10"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Add New Webinar</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Upload the Zoom attendee report — date auto-detected</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
        </div>

        {status === "success" && result ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-foreground font-semibold">{result.webinarId} created</p>
            {result.imported > 0 && (
              <p className="text-muted-foreground text-sm mt-1">{result.imported} unique attendees imported</p>
            )}
            <button
              onClick={() => onSuccess(result!.webinarId)}
              className="mt-4 px-5 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium"
            >
              View Webinar
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {/* CSV upload — primary action */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">
                  Zoom Attendee Report CSV <span className="text-[var(--brand-primary)]">*</span>
                </label>
                <div
                  onClick={() => inputRef.current?.click()}
                  className="border border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-[var(--brand-primary)]/50 transition-colors"
                >
                  <input ref={inputRef} type="file" accept=".csv" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChange(f); }} />
                  {file ? (
                    <div>
                      <p className="text-sm text-foreground">{file.name}</p>
                      {detectedDate && (
                        <p className="text-xs text-[var(--brand-primary)] mt-1">Date detected: {detectedDate}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Click to upload — date will be auto-detected</p>
                  )}
                </div>
              </div>
              {/* Manual date override */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">
                  Webinar Date <span className="text-muted-foreground/60">(override if needed)</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]"
                />
              </div>
            </div>
            {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
            <div className="flex gap-2 mt-5">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={(!date && !detectedDate && !file) || status === "loading"}
                className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              >
                {status === "loading" ? "Creating…" : "Create Webinar"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Webinars() {
  const [webinars, setWebinars] = useState<WebinarDef[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [funnelCache, setFunnelCache] = useState<Record<string, WebinarFunnel>>({});
  const [retentionCache, setRetentionCache] = useState<Record<string, RetentionPoint[]>>({});
  const [retentionBySourceCache, setRetentionBySourceCache] = useState<Record<string, RetentionBySourcePoint[]>>({});
  const [cashBySourceCache, setCashBySourceCache] = useState<Record<string, CashBySourceItem[]>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [funnelMode, setFunnelMode] = useState<"all" | "compare">("all"); // single-view only
  const [cardTraffic, setCardTraffic] = useState<Record<string, "all" | "paid" | "organic">>({});
  const [uploadModal, setUploadModal] = useState<string | null>(null);
  const [addWebinarModal, setAddWebinarModal] = useState(false);
  const [adSpendInput, setAdSpendInput] = useState<Record<string, string>>({});
  const [savingAdSpend, setSavingAdSpend] = useState<string | null>(null);
  const [stageLeadsModal, setStageLeadsModal] = useState<{
    webinarId: string;
    label: string;
    stageKey: string;
    traffic: "all" | "paid" | "organic";
  } | null>(null);
  const [timelineEmail, setTimelineEmail] = useState<string | null>(null);

  // Enhanced metrics state
  const [trafficFilter, setTrafficFilter] = useState<"all" | "paid" | "organic">("all");
  const [appTypeFilter, setAppTypeFilter] = useState<"all" | "full" | "partial">("all");
  const [metricsView, setMetricsView] = useState<"total" | "on-webby" | "post-webby">("total");
  const [enhancedData, setEnhancedData] = useState<Record<string, EnhancedWebinarData | null>>({});
  const [enhancedLoading, setEnhancedLoading] = useState<Set<string>>(new Set());
  const [pitchMinuteInput, setPitchMinuteInput] = useState<Record<string, string>>({});
  const [savingPitchMinute, setSavingPitchMinute] = useState<string | null>(null);

  // Load webinar list on mount
  useEffect(() => {
    fetch("/api/webinar2/list")
      .then((r) => r.json())
      .then((d: { success: boolean; webinars: WebinarDef[] }) => {
        if (d.success) {
          const sorted = [...d.webinars].reverse();
          setWebinars(sorted);
          if (sorted.length) setSelectedIds([sorted[0].id]);
        }
      })
      .catch(console.error);
  }, []);

  const loadWebinar = useCallback(async (id: string) => {
    setLoadingIds((prev) => new Set([...prev, id]));
    try {
      const [fRes, rRes, rbsRes, cbsRes] = await Promise.all([
        fetch(`/api/webinar2/funnel/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/retention/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/retention-by-source/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/cash-by-source/${id}`).then((r) => r.json()),
      ]);
      if (fRes.success) {
        setFunnelCache((prev) => ({ ...prev, [id]: fRes.funnel }));
        if (fRes.funnel.adSpend > 0) {
          setAdSpendInput((prev) => ({ ...prev, [id]: String(fRes.funnel.adSpend) }));
        }
      }
      if (rRes.success) setRetentionCache((prev) => ({ ...prev, [id]: rRes.retention }));
      if (rbsRes.success && rbsRes.retention?.length > 0) {
        setRetentionBySourceCache((prev) => ({ ...prev, [id]: rbsRes.retention }));
      }
      if (cbsRes.success && cbsRes.data?.length > 0) {
        setCashBySourceCache((prev) => ({ ...prev, [id]: cbsRes.data }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  // Load funnels for newly selected webinars
  useEffect(() => {
    for (const id of selectedIds) {
      if (!funnelCache[id] && !loadingIds.has(id)) loadWebinar(id);
    }
  }, [selectedIds, funnelCache, loadingIds, loadWebinar]);

  // Load enhanced metrics when webinar or filters change
  const loadEnhanced = useCallback(async (id: string, traffic: string, appType: string) => {
    const cacheKey = `${id}-${traffic}-${appType}`;
    setEnhancedLoading((prev) => new Set([...prev, id]));
    try {
      const res = await fetch(`/api/webinar2/enhanced/${id}?traffic=${traffic}&appType=${appType}`);
      const d = await res.json() as { success: boolean; data: EnhancedWebinarData };
      if (d.success) {
        setEnhancedData((prev) => ({ ...prev, [cacheKey]: d.data }));
        if (d.data.onWebinar.pitchMinute) {
          setPitchMinuteInput((prev) => ({ ...prev, [id]: String(d.data.onWebinar.pitchMinute) }));
        }
      } else {
        // Mark as failed (null) so we don't retry endlessly on every render
        setEnhancedData((prev) => ({ ...prev, [cacheKey]: null }));
      }
    } catch (e) {
      console.error("Enhanced fetch error:", e);
    } finally {
      setEnhancedLoading((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  const primaryId = selectedIds[0] ?? null;

  const enhancedFetchRef = useRef<string>("");
  useEffect(() => {
    // Load enhanced data for ALL selected webinars (not just primary)
    for (const id of selectedIds) {
      const traffic = selectedIds.length > 1 ? (cardTraffic[id] ?? trafficFilter) : trafficFilter;
      const key = `${id}-${traffic}-${appTypeFilter}`;
      if (!(key in enhancedData) && !enhancedLoading.has(id)) {
        loadEnhanced(id, traffic, appTypeFilter);
      }
    }
    // Track primary for ref-based dedup on filter changes
    const key = `${primaryId}-${trafficFilter}-${appTypeFilter}`;
    if (primaryId && key !== enhancedFetchRef.current) {
      enhancedFetchRef.current = key;
    }
  }, [selectedIds, primaryId, trafficFilter, appTypeFilter, cardTraffic, loadEnhanced, enhancedData, enhancedLoading]);

  const savePitchMinute = async (webinarId: string) => {
    const val = parseInt(pitchMinuteInput[webinarId] ?? "");
    if (isNaN(val) || val < 0) return;
    setSavingPitchMinute(webinarId);
    try {
      await fetch(`/api/webinar2/pitch-minute/${webinarId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pitchMinute: val }),
      });
      loadEnhanced(webinarId, trafficFilter, appTypeFilter);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingPitchMinute(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleStageClick = (webinarId: string, label: string, traffic: "all" | "paid" | "organic" = "all") => {
    const stageKey = STAGE_KEY_MAP[label];
    if (!stageKey) return;
    setStageLeadsModal({ webinarId, label, stageKey, traffic });
  };

  const handleUploadSuccess = (webinarId: string) => {
    setUploadModal(null);
    setFunnelCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    setRetentionCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    setCashBySourceCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    loadWebinar(webinarId);
  };

  const handleAddWebinarSuccess = (newId: string) => {
    setAddWebinarModal(false);
    fetch("/api/webinar2/list")
      .then((r) => r.json())
      .then((d: { success: boolean; webinars: WebinarDef[] }) => {
        if (d.success) {
          const sorted = [...d.webinars].reverse();
          setWebinars(sorted);
          setSelectedIds([newId]);
        }
      })
      .catch(console.error);
  };

  const saveAdSpend = async (webinarId: string) => {
    const val = parseFloat(adSpendInput[webinarId] ?? "");
    if (isNaN(val) || val < 0) return;
    setSavingAdSpend(webinarId);
    try {
      await fetch(`/api/webinar2/ad-spend/${webinarId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adSpend: val }),
      });
      setFunnelCache((prev) => {
        const f = prev[webinarId];
        if (!f) return prev;
        const roas = val > 0 ? f.paidCash / val : 0;
        return { ...prev, [webinarId]: { ...f, adSpend: val, roas } };
      });
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAdSpend(null);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const isSingleView = selectedIds.length <= 1;
  const primaryFunnel = primaryId ? funnelCache[primaryId] ?? null : null;
  const primaryRetention = primaryId ? retentionCache[primaryId] ?? [] : [];
  const primaryRetentionBySource = primaryId ? retentionBySourceCache[primaryId] ?? [] : [];
  const primaryCashBySource = primaryId ? cashBySourceCache[primaryId] ?? [] : [];
  const primaryLoading = primaryId ? loadingIds.has(primaryId) : false;

  const funnelStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.totalOptins },
        ...(primaryFunnel.hasZoomData
          ? [
              { label: "Registrants", value: primaryFunnel.totalRegistrants },
              { label: "Attended", value: primaryFunnel.totalAttended, subLabel: `${Math.round(primaryFunnel.avgDuration)}m avg` },
            ]
          : []),
        { label: "Applications", value: primaryFunnel.totalApps },
        { label: "Calls Booked", value: primaryFunnel.totalCalls },
        { label: "Calls Taken", value: primaryFunnel.totalShowed, subLabel: `${Math.round(primaryFunnel.callShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.totalWins, subLabel: `${primaryFunnel.callToCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.totalCash, isCash: true },
      ]
    : [];

  const organicCloseRate = primaryFunnel && primaryFunnel.organicShowed > 0
    ? (primaryFunnel.organicWins / primaryFunnel.organicShowed) * 100 : 0;
  const paidCloseRate = primaryFunnel && primaryFunnel.paidShowed > 0
    ? (primaryFunnel.paidWins / primaryFunnel.paidShowed) * 100 : 0;
  const organicShowRate = primaryFunnel && primaryFunnel.organicCalls > 0
    ? (primaryFunnel.organicShowed / primaryFunnel.organicCalls) * 100 : 0;
  const paidShowRate = primaryFunnel && primaryFunnel.paidCalls > 0
    ? (primaryFunnel.paidShowed / primaryFunnel.paidCalls) * 100 : 0;

  const organicStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.organicOptins },
        { label: "Applications", value: primaryFunnel.organicApps },
        { label: "Calls Booked", value: primaryFunnel.organicCalls },
        { label: "Calls Taken", value: primaryFunnel.organicShowed, subLabel: `${Math.round(organicShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.organicWins, subLabel: `${organicCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.organicCash, isCash: true },
      ]
    : [];

  const paidStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.paidOptins },
        { label: "Applications", value: primaryFunnel.paidApps },
        { label: "Calls Booked", value: primaryFunnel.paidCalls },
        { label: "Calls Taken", value: primaryFunnel.paidShowed, subLabel: `${Math.round(paidShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.paidWins, subLabel: `${paidCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.paidCash, isCash: true },
      ]
    : [];

  // ── Helpers for multi-card funnels ─────────────────────────────────────────
  const getCardStages = (f: WebinarFunnel, traffic: "all" | "paid" | "organic"): FunnelStage[] => {
    if (traffic === "organic") {
      const sr = f.organicCalls > 0 ? (f.organicShowed / f.organicCalls) * 100 : 0;
      const cr = f.organicShowed > 0 ? (f.organicWins / f.organicShowed) * 100 : 0;
      return [
        { label: "Opt-ins", value: f.organicOptins },
        { label: "Applications", value: f.organicApps },
        { label: "Calls Booked", value: f.organicCalls },
        { label: "Calls Taken", value: f.organicShowed, subLabel: `${Math.round(sr)}% show rate` },
        { label: "Closed", value: f.organicWins, subLabel: `${cr.toFixed(1)}% close rate` },
        { label: "Cash", value: f.organicCash, isCash: true },
      ];
    }
    if (traffic === "paid") {
      const sr = f.paidCalls > 0 ? (f.paidShowed / f.paidCalls) * 100 : 0;
      const cr = f.paidShowed > 0 ? (f.paidWins / f.paidShowed) * 100 : 0;
      return [
        { label: "Opt-ins", value: f.paidOptins },
        { label: "Applications", value: f.paidApps },
        { label: "Calls Booked", value: f.paidCalls },
        { label: "Calls Taken", value: f.paidShowed, subLabel: `${Math.round(sr)}% show rate` },
        { label: "Closed", value: f.paidWins, subLabel: `${cr.toFixed(1)}% close rate` },
        { label: "Cash", value: f.paidCash, isCash: true },
      ];
    }
    const sr = f.totalCalls > 0 ? (f.totalShowed / f.totalCalls) * 100 : 0;
    const cr = f.totalShowed > 0 ? (f.totalWins / f.totalShowed) * 100 : 0;
    return [
      { label: "Opt-ins", value: f.totalOptins },
      { label: "Applications", value: f.totalApps },
      { label: "Calls Booked", value: f.totalCalls },
      { label: "Calls Taken", value: f.totalShowed, subLabel: `${Math.round(sr)}% show rate` },
      { label: "Closed", value: f.totalWins, subLabel: `${cr.toFixed(1)}% close rate` },
      { label: "Cash", value: f.totalCash, isCash: true },
    ];
  };

  // Enhanced data for current selection
  const enhancedKey = primaryId ? `${primaryId}-${trafficFilter}-${appTypeFilter}` : "";
  const enhanced = enhancedKey ? enhancedData[enhancedKey] ?? null : null;
  const enhancedIsLoading = primaryId ? enhancedLoading.has(primaryId) : false;

  // ── Render ─────────────────────────────────────────────────────────────────

  // Helper to render a webinar's content (used in both single and compare views)
  const renderWebinarContent = (id: string, isCompare = false) => {
    const f = funnelCache[id];
    const isLoading = loadingIds.has(id);
    const def = webinars.find((w) => w.id === id);
    const traffic = isCompare ? (cardTraffic[id] ?? "all") : trafficFilter;
    const eKey = `${id}-${traffic}-${appTypeFilter}`;
    const enh = enhancedData[eKey] ?? null;
    const enhLoading = enhancedLoading.has(id);
    const ret = retentionCache[id] ?? [];
    const retBySrc = retentionBySourceCache[id] ?? [];
    const cashBySrc = cashBySourceCache[id] ?? [];
    const retSources = retBySrc.length > 0 ? Object.keys(retBySrc[0]).filter((k) => k !== "minute") : [];

    if (isLoading) return <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">Loading…</div>;
    if (!f) return <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">No data</div>;

    // Build stage data for funnel
    const stages = isCompare ? getCardStages(f, traffic) : (traffic === "all" ? funnelStages : traffic === "organic" ? organicStages : paidStages);

    // Pick which sales block to show based on metricsView toggle
    const activeBlock = enh
      ? (metricsView === "on-webby" ? enh.salesOnWebby : metricsView === "post-webby" ? enh.salesPostWebby : enh.salesTotal)
      : null;
    const totalBlock = enh?.salesTotal ?? null;
    const onBlock = enh?.salesOnWebby ?? null;
    const postBlock = enh?.salesPostWebby ?? null;

    // Build KPIs from active block (enhanced) or fallback to funnel data
    type KpiCard = { l: string; v: string; onVal?: number; postVal?: number; totalVal?: number; isRate?: boolean; isCash?: boolean; clickable?: boolean };
    const salesKpis: KpiCard[] = activeBlock && totalBlock && onBlock && postBlock ? [
      { l: "Opt-ins",       v: fmtN(activeBlock.optins),       totalVal: totalBlock.optins,       onVal: onBlock.optins,       postVal: postBlock.optins, clickable: true },
      { l: "Full Apps",     v: fmtN(activeBlock.fullApps),     totalVal: totalBlock.fullApps,     onVal: onBlock.fullApps,     postVal: postBlock.fullApps, clickable: true },
      { l: "Partial Apps",  v: fmtN(activeBlock.partialApps),  totalVal: totalBlock.partialApps,  onVal: onBlock.partialApps,  postVal: postBlock.partialApps, clickable: true },
      { l: "Calls Booked",  v: fmtN(activeBlock.bookedCalls),  totalVal: totalBlock.bookedCalls,  onVal: onBlock.bookedCalls,  postVal: postBlock.bookedCalls, clickable: true },
      { l: "Calls Taken",   v: fmtN(activeBlock.takenCalls),   totalVal: totalBlock.takenCalls,   onVal: onBlock.takenCalls,   postVal: postBlock.takenCalls, clickable: true },
      { l: "Show Rate",     v: fmtPct(activeBlock.showRate),   isRate: true },
      { l: "Closes",        v: fmtN(activeBlock.closed),       totalVal: totalBlock.closed,       onVal: onBlock.closed,       postVal: postBlock.closed, clickable: true },
      { l: "Close Rate",    v: fmtPct(activeBlock.closeRate),  isRate: true },
      { l: "Cash",          v: fmt$(activeBlock.cash),         totalVal: totalBlock.cash,         onVal: onBlock.cash,         postVal: postBlock.cash, isCash: true, clickable: true },
      { l: "AOV",           v: activeBlock.auv > 0 ? fmt$(activeBlock.auv) : "—", isRate: true },
      { l: "Cash/Call",     v: activeBlock.cashPerCall > 0 ? fmt$(activeBlock.cashPerCall) : "—", isRate: true },
      ...(totalBlock.costPerBooking > 0 ? [{ l: "Cost/Booking", v: metricsView === "total" ? fmt$(totalBlock.costPerBooking) : "—", isRate: true }] : []),
      { l: "App→Booking",   v: fmtPct(activeBlock.appToBookingRate), isRate: true },
      ...(enh.adSpend > 0 ? [{ l: "ROAS", v: activeBlock.roas > 0 ? `${activeBlock.roas.toFixed(1)}x` : "—", isRate: true }] : []),
      ...(totalBlock.chargebackRate > 0 ? [{ l: "Chargeback %", v: metricsView === "total" ? fmtPct(totalBlock.chargebackRate) : "—", isRate: true }] : []),
    ] : [
      { l: "Opt-ins",       v: fmtN(traffic === "paid" ? f.paidOptins : traffic === "organic" ? f.organicOptins : f.totalOptins), clickable: true },
      { l: "Full Apps",     v: fmtN(traffic === "paid" ? f.paidApps : traffic === "organic" ? f.organicApps : f.totalApps), clickable: true },
      { l: "Calls Booked",  v: fmtN(traffic === "paid" ? f.paidCalls : traffic === "organic" ? f.organicCalls : f.totalCalls), clickable: true },
      { l: "Calls Taken",   v: fmtN(traffic === "paid" ? f.paidShowed : traffic === "organic" ? f.organicShowed : f.totalShowed), clickable: true },
      { l: "Show Rate",     v: fmtPct(f.callShowRate), isRate: true },
      { l: "Closes",        v: fmtN(traffic === "paid" ? f.paidWins : traffic === "organic" ? f.organicWins : f.totalWins), clickable: true },
      { l: "Close Rate",    v: fmtPct(f.callToCloseRate), isRate: true },
      { l: "Cash",          v: fmt$(traffic === "paid" ? f.paidCash : traffic === "organic" ? f.organicCash : f.totalCash), clickable: true },
      ...(f.adSpend > 0 ? [{ l: "ROAS", v: f.roas > 0 ? `${f.roas.toFixed(1)}x` : "—", isRate: true }] : []),
    ];

    return (
      <div className="space-y-5">
        {/* Header with name, date, badges, ad spend */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className={`${isCompare ? "text-sm" : "text-base"} font-bold text-foreground`}>{f.webinarName ?? formatDate(def?.date ?? "")}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{formatDate(f.webinarDate)}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {!f.hasZoomData && (
                <span className="text-[10px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded px-2 py-0.5">No Zoom data</span>
              )}
              {!isCompare && (
                <>
                  <button onClick={() => setUploadModal(id)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-card text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors">
                    ↑ Zoom CSV
                  </button>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground">Ad Spend $</span>
                    <input type="number" min="0" value={adSpendInput[id] ?? ""} onChange={(e) => setAdSpendInput((prev) => ({ ...prev, [id]: e.target.value }))} placeholder="0"
                      className="w-20 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                    <button onClick={() => saveAdSpend(id)} disabled={savingAdSpend === id}
                      className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                      {savingAdSpend === id ? "…" : "Save"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Metrics perspective toggle — Total / On Webby / Post Webby */}
          {enh && (
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sales Metrics</h3>
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["total", "on-webby", "post-webby"] as const).map((v) => (
                  <button key={v} onClick={() => setMetricsView(v)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      metricsView === v ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {v === "total" ? "Total" : v === "on-webby" ? "On Webby" : "Post Webby"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sales scorecards with inline proportion bars */}
          <div className={`grid gap-4 ${isCompare ? "grid-cols-3 md:grid-cols-4" : "grid-cols-3 md:grid-cols-4 lg:grid-cols-5"}`}>
            {salesKpis.map((s, i) => {
              // Calculate proportion bar (only on Total view, for count/cash metrics with split data)
              const showBar = metricsView === "total" && !s.isRate && s.totalVal !== undefined && s.onVal !== undefined && s.postVal !== undefined && s.totalVal > 0;
              const onPct = showBar ? (s.onVal! / s.totalVal!) * 100 : 0;
              const canClick = s.clickable && STAGE_KEY_MAP[s.l];

              return (
                <motion.div key={s.l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  onClick={canClick ? () => handleStageClick(id, s.l, traffic as "all" | "paid" | "organic") : undefined}
                  className={`bg-muted/20 border border-border/40 rounded-lg px-3 py-3 ${canClick ? "cursor-pointer hover:border-[var(--brand-primary)]/40 hover:bg-muted/30 transition-colors" : ""}`}>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{s.l}{canClick ? " ↗" : ""}</div>
                  <div className={`${isCompare ? "text-base" : "text-xl"} font-bold text-foreground tabular-nums`}>{s.v}</div>
                  {showBar && (
                    <div className="mt-2">
                      <div className="flex items-center gap-1.5 h-1.5 w-full rounded-full overflow-hidden bg-muted/30">
                        <div className="h-full rounded-full bg-[var(--brand-gradient-start)]" style={{ width: `${onPct}%` }} />
                        <div className="h-full rounded-full bg-[var(--brand-accent)] opacity-60" style={{ width: `${100 - onPct}%`, marginLeft: "-6px" }} />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[9px] text-muted-foreground">{s.isCash ? fmt$(s.onVal!) : fmtN(s.onVal!)} on</span>
                        <span className="text-[9px] text-muted-foreground">{s.isCash ? fmt$(s.postVal!) : fmtN(s.postVal!)} post</span>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Funnel chart */}
        <div className="bg-card border border-border rounded-xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-foreground">Funnel</h3>
            {!isCompare && (
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["all", "compare"] as const).map((mode) => (
                  <button key={mode} onClick={() => setFunnelMode(mode)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      funnelMode === mode ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>{mode}</button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-4">Click any block to view leads</p>
          {!isCompare && funnelMode === "compare" ? (
            <div className="flex gap-3 md:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-center text-muted-foreground mb-3 uppercase tracking-wider">Organic</p>
                <FunnelChart stages={organicStages} gradientId="fgOrganic" onStageClick={(label) => handleStageClick(id, label, "organic")} />
              </div>
              <div className="w-px bg-border/30 self-stretch" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-center text-muted-foreground mb-3 uppercase tracking-wider">Paid</p>
                <FunnelChart stages={paidStages} gradientId="fgPaid" onStageClick={(label) => handleStageClick(id, label, "paid")} />
              </div>
            </div>
          ) : (
            <FunnelChart stages={stages} gradientId={`fg${id}`} onStageClick={(label) => handleStageClick(id, label, isCompare ? traffic : "all")} />
          )}
        </div>

        {/* On-Webinar Metrics */}
        {enh && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">On-Webinar Metrics</h3>
            <div className={`grid ${isCompare ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-4"} gap-4 mb-4`}>
              {[
                { l: "Registrants", v: fmtN(enh.onWebinar.registrants) },
                { l: "CPR", v: enh.onWebinar.cpr > 0 ? fmt$(enh.onWebinar.cpr) : "—" },
                { l: "Attended", v: fmtN(enh.onWebinar.attended) },
                { l: "Show Rate", v: fmtPct(enh.onWebinar.showRate) },
                { l: "Avg Duration", v: `${Math.round(enh.onWebinar.avgDuration)}m` },
                { l: "Booking Rate", v: fmtPct(enh.onWebinar.bookingRate) },
                ...(enh.onWebinar.pitchMinute ? [
                  { l: "Pitch Start", v: `${enh.onWebinar.pitchMinute}m` },
                  { l: "@ Pitch", v: fmtPct(enh.onWebinar.retentionAtPitch) },
                ] : []),
                ...(enh.dials.totalDials > 0 ? [
                  { l: "Total Dials", v: fmtN(enh.dials.totalDials) },
                  { l: "Pickup Rate", v: fmtPct(enh.dials.pickupRate) },
                ] : []),
              ].map((s, i) => (
                <motion.div key={s.l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                  <div className={`${isCompare ? "text-sm" : "text-lg"} font-bold text-foreground`}>{s.v}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{s.l}</div>
                </motion.div>
              ))}
            </div>
            {!isCompare && (
              <div className="flex items-center gap-2 pt-3 border-t border-border/30">
                <span className="text-[10px] text-muted-foreground">Pitch starts at minute</span>
                <input type="number" min="0" placeholder="e.g. 45" value={pitchMinuteInput[id] ?? ""}
                  onChange={(e) => setPitchMinuteInput((prev) => ({ ...prev, [id]: e.target.value }))}
                  className="w-16 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                <button onClick={() => savePitchMinute(id)} disabled={savingPitchMinute === id}
                  className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                  {savingPitchMinute === id ? "…" : "Set"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Zoom Attendance + Retention */}
        {f.hasZoomData && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Zoom Attendance</h3>
            <div className={`grid ${isCompare ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"} gap-4 mb-5`}>
              {[
                { l: "Registrants",  v: fmtN(f.totalRegistrants) },
                { l: "Attended",     v: fmtN(f.totalAttended) },
                { l: "Show Rate",    v: fmtPct(f.showRate) },
                { l: "Avg Duration", v: `${Math.round(f.avgDuration)}m` },
              ].map((s, i) => (
                <motion.div key={s.l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                  <div className={`${isCompare ? "text-sm" : "text-lg"} font-bold text-foreground`}>{s.v}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{s.l}</div>
                </motion.div>
              ))}
            </div>
            {ret.length > 0 && (
              <>
                <p className="text-xs text-muted-foreground mb-3">Audience retention — % still watching by minute</p>
                <ResponsiveContainer width="100%" height={isCompare ? 140 : 180}>
                  <AreaChart data={ret} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`retGrad${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={branding.colors.gradient.start} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={branding.colors.gradient.start} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${v}m`} />
                    <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      labelFormatter={(v) => `${v} min`} formatter={(_v: number, _name: string, item: { payload: RetentionPoint }) => [String(item.payload.count), "Still watching"]} />
                    <Area type="monotone" dataKey="pct" stroke={branding.colors.gradient.start} strokeWidth={2} fill={`url(#retGrad${id})`} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
            {/* Retention by source */}
            {retSources.length > 1 && (
              <div className="mt-5 pt-4 border-t border-border/30">
                <RetentionBySourceChart data={retBySrc} sources={retSources} colors={branding.colors.chart} />
              </div>
            )}
          </div>
        )}

        {/* Cash by source — own card, shown regardless of zoom data */}
        {cashBySrc.length > 0 && !isCompare && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Cash Collected by Traffic Source</h3>
            <CashBySourceChart data={cashBySrc} />
          </div>
        )}

        {/* Front-End Low Ticket */}
        {enh && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Front-End Low Ticket</h3>
            {enh.lowTicket.purchasers > 0 ? (
              <div className={`grid ${isCompare ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"} gap-4`}>
                {[
                  { l: "Purchasers", v: fmtN(enh.lowTicket.purchasers) },
                  { l: "Purchase Rate", v: fmtPct(enh.lowTicket.purchaseRate) },
                  { l: "Liquidation %", v: fmtPct(enh.lowTicket.liquidationPct) },
                  { l: "AOV", v: fmt$(enh.lowTicket.auv) },
                  { l: "Webinar Show-up", v: fmtPct(enh.lowTicket.purchaserWebinarShowRate) },
                  { l: "→ Booking", v: fmtPct(enh.lowTicket.purchaserToBookingRate) },
                  { l: "→ Show", v: fmtPct(enh.lowTicket.purchaserToShowRate) },
                  { l: "→ Close", v: fmtPct(enh.lowTicket.purchaserToCloseRate) },
                  { l: "Revenue", v: fmt$(enh.lowTicket.totalRevenue) },
                ].map((s, i) => (
                  <motion.div key={s.l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                    <div className={`${isCompare ? "text-sm" : "text-lg"} font-bold text-foreground`}>{s.v}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{s.l}</div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">No low-ticket purchases matched for this webinar</div>
            )}
          </div>
        )}

        {/* Enhanced loading indicator */}
        {enhLoading && (
          <div className="bg-card border border-border rounded-xl p-8 flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Loading enhanced metrics…</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Webinars</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {selectedIds.length > 1 ? `Comparing ${selectedIds.length} webinars` : "Funnel performance by event"}
          </p>
        </div>
        <button onClick={() => setAddWebinarModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--brand-primary)] text-white text-xs font-medium hover:opacity-90 transition-opacity">
          + Add Webinar
        </button>
      </div>

      {/* Webinar selector */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {webinars.map((w) => (
          <button key={w.id} onClick={() => toggleSelect(w.id)}
            className={`flex-shrink-0 px-3 py-2 rounded-xl border text-left transition-all ${
              selectedIds.includes(w.id)
                ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10"
                : "border-border bg-card hover:border-border/70"
            }`}>
            <div className={`text-xs font-bold whitespace-nowrap ${selectedIds.includes(w.id) ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
              {formatDate(w.date)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{w.id}</div>
          </button>
        ))}
      </div>

      {/* Split by traffic filter — under webinar selector */}
      {primaryId && (
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Split by traffic:</span>
            <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
              {(["all", "organic", "paid"] as const).map((t) => (
                <button key={t} onClick={() => { setTrafficFilter(t); if (!isSingleView) { const updated: Record<string, "all" | "paid" | "organic"> = {}; selectedIds.forEach((sid) => { updated[sid] = t; }); setCardTraffic(updated); } }}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                    trafficFilter === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Apps:</span>
            <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
              {(["all", "full", "partial"] as const).map((t) => (
                <button key={t} onClick={() => setAppTypeFilter(t)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                    appTypeFilter === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                  }`}>{t}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SINGLE WEBINAR VIEW ──────────────────────────────────────────────── */}
      {isSingleView && primaryId && renderWebinarContent(primaryId)}
      {isSingleView && !primaryId && (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">Select a webinar</div>
      )}

      {/* ── MULTI-WEBINAR COMPARISON VIEW ────────────────────────────────────── */}
      {!isSingleView && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {selectedIds.map((id) => (
            <div key={id}>
              {renderWebinarContent(id, true)}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {uploadModal && (
          <ZoomUploadModal
            webinarId={uploadModal}
            webinarName={webinars.find((w) => w.id === uploadModal)?.name ?? uploadModal}
            onClose={() => setUploadModal(null)}
            onSuccess={() => handleUploadSuccess(uploadModal)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addWebinarModal && (
          <AddWebinarModal
            onClose={() => setAddWebinarModal(false)}
            onSuccess={handleAddWebinarSuccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {stageLeadsModal && (
          <FunnelLeadsModal
            webinarId={stageLeadsModal.webinarId}
            webinarName={funnelCache[stageLeadsModal.webinarId]?.webinarName ?? stageLeadsModal.webinarId}
            stageLabel={stageLeadsModal.label}
            stageKey={stageLeadsModal.stageKey}
            traffic={stageLeadsModal.traffic}
            onClose={() => setStageLeadsModal(null)}
            onLeadClick={(email) => setTimelineEmail(email)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {timelineEmail && (
          <LeadTimelineDrawer
            email={timelineEmail}
            onClose={() => setTimelineEmail(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
